import { TFunction } from 'i18next'
import {
  BusinessType,
  DeliveryTimeframe,
  FulfillmentMethod
} from 'generated/graphql'
import { getMonthsOptions, mapEnumToOptions } from 'utils/enum-tools'
import { FieldType, IFormConfig } from 'types'
import { yesNoOptions } from 'consts'
import { CurrencySymbolDollar } from '@pay-com/icons'
import {
  BusinessActivityForm,
  businessActivitySchema
} from './business-activity-schema'
import { MccCodesField } from 'components/MccCodes'
import { CreditCardProcessingFields } from 'components/CreditCardProcessingFields'
import { AmexMarketingMaterialsField } from 'components/AmexMarketingMaterialsField'

export const getBusinessActivityConfig = (
  t: TFunction
): IFormConfig<BusinessActivityForm> => ({
  schema: businessActivitySchema,
  fields: [
    {
      name: 'businessStructureDetails.businessType',
      label: t('businessActivity.fields.businessType.label'),
      placeholder: t('businessActivity.fields.businessType.placeholder'),
      type: FieldType.SELECT,
      tooltip: t('businessActivity.fields.businessType.tooltip'),
      options: mapEnumToOptions({
        [BusinessType.Internet]: t(
          'businessActivity.fields.businessType.options.internet'
        ),
        [BusinessType.Moto]: t(
          'businessActivity.fields.businessType.options.moto'
        ),
        [BusinessType.Other]: t(
          'businessActivity.fields.businessType.options.other'
        ),
        [BusinessType.Pharmacy]: t(
          'businessActivity.fields.businessType.options.pharmacy'
        ),
        [BusinessType.Restaurant]: t(
          'businessActivity.fields.businessType.options.restaurant'
        ),
        [BusinessType.Retail]: t(
          'businessActivity.fields.businessType.options.retail'
        ),
        [BusinessType.RetailWithTips]: t(
          'businessActivity.fields.businessType.options.retail_with_tips'
        ),
        [BusinessType.Supermarket]: t(
          'businessActivity.fields.businessType.options.supermarket'
        ),
        [BusinessType.Utility]: t(
          'businessActivity.fields.businessType.options.utility'
        )
      })
    },
    {
      name: 'businessStructureDetails.businessTypeDescription',
      label: t('businessActivity.fields.businessTypeDescription.label'),
      tooltip: t('businessActivity.fields.businessTypeDescription.tooltip'),
      placeholder: t(
        'businessActivity.fields.businessTypeDescription.placeholder'
      ),
      ignoreField: methods =>
        methods.watch('businessStructureDetails.businessType') !==
        BusinessType.Other
    },
    {
      name: 'businessProcessingInfo.numberOfPhysicalLocations',
      htmlType: 'number',
      label: t('businessActivity.fields.numberOfPhysicalLocations.label'),
      placeholder: t(
        'businessActivity.fields.numberOfPhysicalLocations.placeholder'
      ),
      tooltip: t('businessActivity.fields.numberOfPhysicalLocations.tooltip'),
      ignoreField: methods =>
        methods.watch('businessStructureDetails.businessType') ===
        BusinessType.Internet
    },
    {
      name: 'businessProcessingInfo.applyingForAdditionalLocations',
      type: FieldType.CHECKBOX,
      checkboxLabel: t(
        'businessActivity.fields.applyingForAdditionalLocations.checkboxLabel'
      ),
      ignoreField: methods =>
        methods.watch('businessStructureDetails.businessType') ===
        BusinessType.Internet
    },
    {
      name: 'mccCodes',
      type: FieldType.CUSTOM,
      label: t('businessActivity.fields.mccCodes.label'),
      tooltip: t('businessActivity.fields.mccCodes.tooltip'),
      CustomComponent: MccCodesField
    },
    {
      name: 'businessDetails.industryDescription',
      type: FieldType.TEXTAREA,
      label: t('businessActivity.fields.industryDescription.label'),
      placeholder: t('businessActivity.fields.industryDescription.placeholder'),
      tooltip: t('businessActivity.fields.industryDescription.tooltip')
    },
    {
      name: 'creditCardProcessingMethods',
      type: FieldType.CUSTOM,
      label: t('businessActivity.fields.ecommerceBasisPoints.label'),
      CustomComponent: CreditCardProcessingFields,
      customComponentProps: {
        placeholder: t(
          'businessActivity.fields.creditCardProcessingMethods.placeholder'
        ),
        fields: [
          {
            name: 'businessActivity.ecommerceBasisPoints',
            label: t('businessActivity.fields.ecommerceBasisPoints.label'),
            placeholder: t(
              'businessActivity.fields.ecommerceBasisPoints.placeholder'
            ),
            tooltip: t('businessActivity.fields.ecommerceBasisPoints.tooltip')
          },
          {
            name: 'businessActivity.manuallyKeyedCnpMotoBasisPoints',
            label: t(
              'businessActivity.fields.manuallyKeyedCnpMotoBasisPoints.label'
            ),
            placeholder: t(
              'businessActivity.fields.manuallyKeyedCnpMotoBasisPoints.placeholder'
            ),
            tooltip: t(
              'businessActivity.fields.manuallyKeyedCnpMotoBasisPoints.tooltip'
            )
          },
          {
            name: 'businessActivity.manuallyKeyedCpImprintBasisPoints',
            label: t(
              'businessActivity.fields.manuallyKeyedCpImprintBasisPoints.label'
            ),
            placeholder: t(
              'businessActivity.fields.manuallyKeyedCpImprintBasisPoints.placeholder'
            ),
            tooltip: t(
              'businessActivity.fields.manuallyKeyedCpImprintBasisPoints.tooltip'
            )
          },
          {
            name: 'businessActivity.cardPresentBasisPoints',
            label: t('businessActivity.fields.cardPresentBasisPoints.label'),
            placeholder: t(
              'businessActivity.fields.cardPresentBasisPoints.placeholder'
            ),
            tooltip: t('businessActivity.fields.cardPresentBasisPoints.tooltip')
          },
          {
            name: 'businessActivity.businessToBusinessBasisPoints',
            label: t(
              'businessActivity.fields.businessToBusinessBasisPoints.label'
            ),
            placeholder: t(
              'businessActivity.fields.businessToBusinessBasisPoints.placeholder'
            ),
            tooltip: t(
              'businessActivity.fields.businessToBusinessBasisPoints.tooltip'
            )
          }
        ]
      }
    },
    {
      name: 'businessProcessingInfo.isWebsitePciCompliant',
      type: FieldType.RADIO,
      options: yesNoOptions,
      label: t('businessActivity.fields.isWebsitePciCompliant.label'),
      tooltip: t('businessActivity.fields.isWebsitePciCompliant.tooltip')
    },
    {
      name: 'fulfillmentDetails.fulfillmentMethod',
      type: FieldType.RADIO,
      options: [
        { value: FulfillmentMethod.ManagedByThirdParty, label: 'Yes' },
        { value: FulfillmentMethod.ManagedInHouse, label: 'No' }
      ],
      label: t('businessActivity.fields.fulfillmentMethod.label'),
      tooltip: t('businessActivity.fields.fulfillmentMethod.tooltip')
    },
    {
      name: 'fulfillmentDetails.fulfillmentMethodDescription',
      type: FieldType.TEXTAREA,
      label: t('businessActivity.fields.fulfillmentMethodDescription.label'),
      tooltip: t(
        'businessActivity.fields.fulfillmentMethodDescription.tooltip'
      ),
      placeholder: t(
        'businessActivity.fields.fulfillmentMethodDescription.placeholder'
      ),
      ignoreField: methods =>
        methods.watch('fulfillmentDetails.fulfillmentMethod') !==
        FulfillmentMethod.ManagedByThirdParty
    },
    {
      name: 'businessActivity.monthlyCardVolumeAmount',
      htmlType: 'number',
      label: t('businessActivity.fields.monthlyCardVolumeAmount.label'),
      placeholder: t(
        'businessActivity.fields.monthlyCardVolumeAmount.placeholder'
      ),
      tooltip: t('businessActivity.fields.monthlyCardVolumeAmount.tooltip'),
      icon: (
        <CurrencySymbolDollar className="text-gray-500" fill="currentColor" />
      )
    },
    {
      name: 'businessActivity.maximumMonthlyCardVolumeAmount',
      htmlType: 'number',
      label: t('businessActivity.fields.maximumMonthlyCardVolumeAmount.label'),
      placeholder: t(
        'businessActivity.fields.maximumMonthlyCardVolumeAmount.placeholder'
      ),
      tooltip: t(
        'businessActivity.fields.maximumMonthlyCardVolumeAmount.tooltip'
      ),
      icon: (
        <CurrencySymbolDollar className="text-gray-500" fill="currentColor" />
      )
    },
    {
      name: 'businessActivity.averageTransactionAmount',
      htmlType: 'number',
      label: t('businessActivity.fields.averageTransactionAmount.label'),
      placeholder: t(
        'businessActivity.fields.averageTransactionAmount.placeholder'
      ),
      tooltip: t('businessActivity.fields.averageTransactionAmount.tooltip'),
      icon: (
        <CurrencySymbolDollar className="text-gray-500" fill="currentColor" />
      )
    },
    {
      name: 'businessActivity.maximumTransactionAmount',
      htmlType: 'number',
      label: t('businessActivity.fields.maximumTransactionAmount.label'),
      placeholder: t(
        'businessActivity.fields.maximumTransactionAmount.placeholder'
      ),
      tooltip: t('businessActivity.fields.maximumTransactionAmount.tooltip'),
      icon: (
        <CurrencySymbolDollar className="text-gray-500" fill="currentColor" />
      )
    },
    {
      name: 'businessActivity.requestingPayoutsToCustomers',
      type: FieldType.RADIO,
      options: yesNoOptions,
      label: t('businessActivity.fields.requestingPayoutsToCustomers.label'),
      tooltip: t('businessActivity.fields.requestingPayoutsToCustomers.tooltip')
    },
    {
      name: 'businessProcessingInfo.hasAcceptedCreditCardsBefore',
      type: FieldType.RADIO,
      options: yesNoOptions,
      label: t('businessActivity.fields.hasAcceptedCreditCardsBefore.label'),
      tooltip: t('businessActivity.fields.hasAcceptedCreditCardsBefore.tooltip')
    },
    {
      name: 'businessProcessingInfo.hasAcceptedCcBeforeDescription',
      label: t('businessActivity.fields.hasAcceptedCcBeforeDescription.label'),
      placeholder: t(
        'businessActivity.fields.hasAcceptedCcBeforeDescription.placeholder'
      ),
      tooltip: t(
        'businessActivity.fields.hasAcceptedCcBeforeDescription.tooltip'
      ),
      ignoreField: methods =>
        !methods.watch('businessProcessingInfo.hasAcceptedCreditCardsBefore')
    },
    {
      name: 'businessProcessingInfo.terminatedByPreviousProcessor',
      type: FieldType.RADIO,
      options: yesNoOptions,
      label: t('businessActivity.fields.terminatedByPreviousProcessor.label'),
      tooltip: t(
        'businessActivity.fields.terminatedByPreviousProcessor.tooltip'
      )
    },
    {
      name: 'businessProcessingInfo.hasExistingPaycomMerchant',
      type: FieldType.RADIO,
      options: yesNoOptions,
      label: t('businessActivity.fields.hasExistingPaycomMerchant.label'),
      tooltip: t('businessActivity.fields.hasExistingPaycomMerchant.tooltip')
    },
    {
      name: 'businessProcessingInfo.existingPaycomMerchantId',
      label: t('businessActivity.fields.existingPaycomMerchantId.label'),
      placeholder: t(
        'businessActivity.fields.existingPaycomMerchantId.placeholder'
      ),
      tooltip: t('businessActivity.fields.existingPaycomMerchantId.tooltip'),
      ignoreField: methods =>
        !methods.watch('businessProcessingInfo.hasExistingPaycomMerchant')
    },
    {
      name: 'fulfillmentDetails.deliveryTimeframe',
      type: FieldType.SELECT,
      label: t('businessActivity.fields.deliveryTimeframe.label'),
      placeholder: t('businessActivity.fields.deliveryTimeframe.placeholder'),
      tooltip: t('businessActivity.fields.deliveryTimeframe.tooltip'),
      options: mapEnumToOptions({
        [DeliveryTimeframe.Immediate]: t(
          'businessActivity.fields.deliveryTimeframe.options.immediate'
        ),
        [DeliveryTimeframe.Below3days]: t(
          'businessActivity.fields.deliveryTimeframe.options.below3days'
        ),
        [DeliveryTimeframe.Below7days]: t(
          'businessActivity.fields.deliveryTimeframe.options.below7days'
        ),
        [DeliveryTimeframe.Below15days]: t(
          'businessActivity.fields.deliveryTimeframe.options.below15days'
        ),
        [DeliveryTimeframe.Below20days]: t(
          'businessActivity.fields.deliveryTimeframe.options.below20days'
        ),
        [DeliveryTimeframe.Above21days]: t(
          'businessActivity.fields.deliveryTimeframe.options.above21days'
        )
      })
    },
    {
      name: 'businessProcessingInfo.returnAndRefundPolicy',
      type: FieldType.TEXTAREA,
      label: t('businessActivity.fields.returnAndRefundPolicy.label'),
      placeholder: t(
        'businessActivity.fields.returnAndRefundPolicy.placeholder'
      ),
      tooltip: t('businessActivity.fields.returnAndRefundPolicy.tooltip')
    },
    {
      name: 'businessActivity.annualAmexVolumeExceeds',
      type: FieldType.RADIO,
      options: yesNoOptions,
      label: t('businessActivity.fields.annualAmexVolumeExceeds.label'),
      tooltip: t('businessActivity.fields.annualAmexVolumeExceeds.tooltip')
    },
    {
      name: 'businessActivity.esaNumber',
      label: t('businessActivity.fields.esaNumber.label'),
      placeholder: t('businessActivity.fields.esaNumber.placeholder'),
      tooltip: t('businessActivity.fields.esaNumber.tooltip'),
      ignoreField: methods =>
        !methods.watch('businessActivity.annualAmexVolumeExceeds')
    },
    {
      name: 'businessProcessingInfo.wantsAmexMarketingMaterials',
      type: FieldType.CUSTOM,
      label: t('businessActivity.fields.wantsAmexMarketingMaterials.label'),
      tooltip: t('businessActivity.fields.wantsAmexMarketingMaterials.tooltip'),
      CustomComponent: AmexMarketingMaterialsField
    },
    {
      name: 'businessActivity.seasonalBusiness',
      type: FieldType.RADIO,
      options: yesNoOptions,
      label: t('businessActivity.fields.seasonalBusiness.label'),
      tooltip: t('businessActivity.fields.seasonalBusiness.tooltip')
    },
    {
      name: 'businessActivity.seasonalBusinessMonths',
      type: FieldType.MULTI_SELECT,
      label: t('businessActivity.fields.seasonalBusinessMonths.label'),
      placeholder: t(
        'businessActivity.fields.seasonalBusinessMonths.placeholder'
      ),
      tooltip: t('businessActivity.fields.seasonalBusinessMonths.tooltip'),
      options: getMonthsOptions(t),
      ignoreField: methods =>
        !methods.watch('businessActivity.seasonalBusiness')
    }
  ]
})
